<template>
  <div v-if="shouldShowRecommendations" data-cy="new-recommendation-teaser">
    <h2>{{ title }}</h2>
    <HgLoadWhenInViewport>
      <ListingCarousel
        :listings="recommendedListings"
        :class="$style.carousel"
        :nextButtonTitle="t('newConstruction.nextApartment')"
        :prevButtonTitle="t('newConstruction.previousApartment')"
        shouldShowNewConstructionBadge
        @listingClicked="onRecommendationClick"
        @listingCarouselMounted="onRecommendationsMounted"
        @listingCarouselChanged="onRecommendationsChanged"
      />
    </HgLoadWhenInViewport>
  </div>
</template>

<script lang="ts" setup>
import { HgLoadWhenInViewport } from '@smg-real-estate/domus';
import { Listing } from '@smg-real-estate/hg-rets-schema';
import { ListingItem } from '@smg-real-estate/hg-search-libs';
import { computed, ref } from 'vue';

import ListingCarousel from '@/components/ListingCarousel/ListingCarousel.vue';
import { GTMPageType, InteractionElement } from '@/helpers/GTM/GTMParameters';
import { NewConstructionDataWithOtherListings } from '@/models';
import {
  getInteractionElementOfRecommendationChange,
  trackRecommendationClicked,
  trackRecommendationsChanged,
  trackRecommendationsLoaded,
} from '@/trackers/Recommendations/Recommendations.tracking';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

defineOptions({
  name: 'NewConstructionRecommendations',
});

interface Props {
  newConstructionProject?: NewConstructionDataWithOtherListings | null;
  title: string;
}
const props = withDefaults(defineProps<Props>(), {
  newConstructionProject: null,
});

const visibleRecommendations = ref(0);
const activeIndex = ref(0);

const shouldShowRecommendations = computed((): boolean => {
  return !!props.newConstructionProject && recommendedListings.value.length > 0;
});
const recommendedListings = computed((): Listing[] => {
  return props.newConstructionProject?.otherListings ?? [];
});
const recommendedListingItems = computed((): ListingItem[] => {
  return recommendedListings.value.map((listing) => {
    return { id: listing.id, listing };
  }) as ListingItem[];
});

const onRecommendationsMounted = (visibleRecommendationsNumber: number): void => {
  visibleRecommendations.value = visibleRecommendationsNumber;
  trackRecommendationsLoaded(recommendedListingItems.value, {
    gtmPageType: GTMPageType.DETAIL_PAGE,
    activeIndex: activeIndex.value,
    visibleItems: visibleRecommendationsNumber,
    isNewConstruction: true,
    listerId: recommendedListings.value[0]?.lister?.id,
  });
};
const onRecommendationClick = (index: number): void => {
  trackRecommendationClicked(recommendedListingItems.value, {
    gtmPageType: GTMPageType.DETAIL_PAGE,
    activeIndex: activeIndex.value,
    visibleItems: visibleRecommendations.value,
    selectedIndex: index,
    isNewConstruction: true,
    listerId: recommendedListings.value[0]?.lister?.id,
  });
};
const onRecommendationsChanged = (activeIndexNumber: number): void => {
  //  The slider transmits changes even if no changes have occurred (especially from the keyboard listener).
  //  If all items are displayed, then no changes or actions are needed.
  if (visibleRecommendations.value === recommendedListingItems.value.length) {
    return;
  }

  const changeInteraction: InteractionElement | undefined =
    getInteractionElementOfRecommendationChange({
      oldIndex: activeIndex.value,
      newIndex: activeIndexNumber,
      lastIndex: recommendedListingItems.value.length - 1,
    });

  activeIndex.value = activeIndexNumber;
  trackRecommendationsChanged(recommendedListingItems.value, {
    gtmPageType: GTMPageType.DETAIL_PAGE,
    activeIndex: activeIndexNumber,
    visibleItems: visibleRecommendations.value,
    isNewConstruction: true,
    changeInteraction,
    listerId: recommendedListings.value[0]?.lister?.id,
  });
};
</script>

<style lang="scss" module>
@import '@/assets/variables';

.carousel {
  margin-bottom: $space-150 * 2;
}
</style>
